import type { SortOption, GeoPoint } from '@/types/listing';

// `GeoPoint` is the shape already used for bbox corners; a proximity origin is the
// same thing and must not become a second, drifting declaration of it.
export type { GeoPoint };

/**
 * (0,0) is the Gulf of Guinea, and it is what the MLS feed carries for a listing
 * the geocode job has not reached. Treating it as a place ranks the entire Bay
 * Area by distance from an ocean.
 */
export function isUsablePoint(p: GeoPoint | undefined | null): p is GeoPoint {
  return !!p && !(p.lat === 0 && p.lon === 0);
}

/**
 * Enforce the one invariant proximity search has: `sort=nearest` requires a
 * `near` origin.
 *
 * The server refuses the pair half-set with a **400 on the whole search** —
 * deliberately, rather than silently re-sorting — so a client that emits it
 * breaks search entirely rather than degrading.
 *
 * On the web the pair comes apart in two ordinary ways. `sort` round-trips
 * through the URL, so `?sort=nearest` is a link somebody can bookmark or paste;
 * and it is persisted inside saved-search filters. `near`, meanwhile, is a live
 * browser geolocation fix that exists only in the session that asked for it. A
 * bookmarked proximity search opened tomorrow is precisely the broken request,
 * which is why this normalises rather than trusting the caller.
 */
export function normalizeProximity<T extends { sort?: SortOption; near?: GeoPoint }>(
  params: T
): T {
  const near = isUsablePoint(params.near) ? params.near : undefined;
  if (params.sort === 'nearest' && !near) {
    return { ...params, near: undefined, sort: 'newest' as SortOption };
  }
  return { ...params, near };
}
